/**
 * Sector operations — Evidence Export, Retention & Legal Hold Service (R39 A11.a, SO-P73).
 * Typed reads, subscriptions, and callable wrappers for exports, retention, and legal holds.
 */

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  RequestEvidenceExportInput,
  GetEvidenceExportDownloadInput,
  SetRetentionSettingsInput,
  SetLegalHoldInput,
  RequestLegalHoldLiftInput,
  ConfirmLegalHoldLiftInput,
  CancelLegalHoldLiftInput,
  RestoreFromRetentionBinInput,
  OpsEvidenceExportDoc,
  OpsRetentionSettingsDoc,
  OpsLegalHoldDoc,
  OpsRetentionBinDoc,
  OpsRetentionNoticeDoc,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function requestEvidenceExport(
  input: RequestEvidenceExportInput,
): Promise<{ exportId: string; status: string }> {
  return callable<RequestEvidenceExportInput, { exportId: string; status: string }>(
    'requestEvidenceExport',
    input,
  )
}

export async function getEvidenceExportDownload(
  input: GetEvidenceExportDownloadInput,
): Promise<{ urls: Record<string, string>; expiresAt: string }> {
  return callable<GetEvidenceExportDownloadInput, { urls: Record<string, string>; expiresAt: string }>(
    'getEvidenceExportDownload',
    input,
  )
}

export async function setRetentionSettings(
  input: SetRetentionSettingsInput,
): Promise<{ success: boolean; version: number }> {
  return callable<SetRetentionSettingsInput, { success: boolean; version: number }>(
    'setRetentionSettings',
    input,
  )
}

export async function setLegalHold(
  input: SetLegalHoldInput,
): Promise<{ holdId: string; status: string }> {
  return callable<SetLegalHoldInput, { holdId: string; status: string }>(
    'setLegalHold',
    input,
  )
}

export async function requestLegalHoldLift(
  input: RequestLegalHoldLiftInput,
): Promise<{ holdId: string; status: string }> {
  return callable<RequestLegalHoldLiftInput, { holdId: string; status: string }>(
    'requestLegalHoldLift',
    input,
  )
}

export async function confirmLegalHoldLift(
  input: ConfirmLegalHoldLiftInput,
): Promise<{ holdId: string; status: string }> {
  return callable<ConfirmLegalHoldLiftInput, { holdId: string; status: string }>(
    'confirmLegalHoldLift',
    input,
  )
}

export async function cancelLegalHoldLift(
  input: CancelLegalHoldLiftInput,
): Promise<{ holdId: string; status: string }> {
  return callable<CancelLegalHoldLiftInput, { holdId: string; status: string }>(
    'cancelLegalHoldLift',
    input,
  )
}

export async function restoreFromRetentionBin(
  input: RestoreFromRetentionBinInput,
): Promise<{ restoredId: string; source: string }> {
  return callable<RestoreFromRetentionBinInput, { restoredId: string; source: string }>(
    'restoreFromRetentionBin',
    input,
  )
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export function subscribeEvidenceExports(
  workspaceId: string,
  options: { uid: string; isAdmin: boolean },
  callback: (exports: OpsEvidenceExportDoc[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_exports')
  const q = options.isAdmin
    ? query(
        coll,
        where('workspaceId', '==', workspaceId),
        orderBy('createdAt', 'desc'),
        limit(50),
      )
    : query(
        coll,
        where('workspaceId', '==', workspaceId),
        where('requestedBy', '==', options.uid),
        orderBy('createdAt', 'desc'),
        limit(50),
      )

  return onSnapshot(
    q,
    (snap) => {
      const items: OpsEvidenceExportDoc[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsEvidenceExportDoc, 'id'>),
      }))
      callback(items)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export function subscribeRetentionSettings(
  workspaceId: string,
  callback: (settings: OpsRetentionSettingsDoc | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback(null)
    return () => undefined
  }
  const ref = doc(firestoreDb, 'ops_retention_settings', workspaceId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        callback(null)
        return
      }
      callback({
        workspaceId: snap.id,
        ...(snap.data() as Omit<OpsRetentionSettingsDoc, 'workspaceId'>),
      })
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export function subscribeLegalHolds(
  workspaceId: string,
  callback: (holds: OpsLegalHoldDoc[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_legal_holds')
  const q = query(coll, where('workspaceId', '==', workspaceId))
  return onSnapshot(
    q,
    (snap) => {
      const items: OpsLegalHoldDoc[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsLegalHoldDoc, 'id'>),
      }))
      items.sort((a, b) => {
        const aVal = a.setAt
        const bVal = b.setAt
        const aTime =
          aVal && typeof aVal === 'object' && 'toMillis' in aVal && typeof aVal.toMillis === 'function'
            ? aVal.toMillis()
            : aVal instanceof Date
              ? aVal.getTime()
              : typeof aVal === 'string'
                ? new Date(aVal).getTime()
                : 0
        const bTime =
          bVal && typeof bVal === 'object' && 'toMillis' in bVal && typeof bVal.toMillis === 'function'
            ? bVal.toMillis()
            : bVal instanceof Date
              ? bVal.getTime()
              : typeof bVal === 'string'
                ? new Date(bVal).getTime()
                : 0
        return bTime - aTime
      })
      callback(items)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export function subscribeRetentionBin(
  workspaceId: string,
  callback: (entries: OpsRetentionBinDoc[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_retention_bin')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    orderBy('purgeAt', 'asc'),
    limit(200),
  )
  return onSnapshot(
    q,
    (snap) => {
      const items: OpsRetentionBinDoc[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<OpsRetentionBinDoc, 'id'>),
      }))
      callback(items)
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}

export function subscribeLatestRetentionNotice(
  workspaceId: string,
  callback: (notice: OpsRetentionNoticeDoc | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback(null)
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_retention_notices')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    orderBy('dayKey', 'desc'),
    limit(1),
  )
  return onSnapshot(
    q,
    (snap) => {
      if (snap.empty) {
        callback(null)
        return
      }
      const docSnap = snap.docs[0]
      callback({
        id: docSnap.id,
        ...(docSnap.data() as Omit<OpsRetentionNoticeDoc, 'id'>),
      })
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    },
  )
}
